import { Box, Tooltip } from '@mantine/core'
import { useTranslations } from 'next-intl'

import LockIcon from '@/assets/icons/lock.svg'

interface NoQuoteLockIconProps {
  size?: number
  /**
   * When true, the tooltip's hover target fills the host cell instead of just
   * the 16px glyph, so hovering anywhere in the cell shows the tooltip
   * (OG-9099 feedback). The lock stays centered. The host cell must be
   * positioned — the shared price-cell shell already is (`position: relative`).
   */
  fillParent?: boolean
}

/**
 * Adapted from cdc-web `src/components/knockOuts/NoQuoteLockIcon.tsx`。
 * 差异：
 * - cdc-market 没有 `@/components/basic/Tooltip` 封装，直接用 Mantine Tooltip
 *   （同一组 props：label/events/withinPortal，注释里说明为什么 withinPortal）。
 * - 只保留 `default` variant（lock.svg 描边，redesigned 卡片用）；markets 页的
 *   filled variant（lock-filled.svg）本区用不到，不抄。
 *
 * Hovering the lock shows "No price available now"; the copy is also the icon's
 * accessible name so screen readers announce it without hover.
 */
export default function NoQuoteLockIcon({
  size = 16,
  fillParent = false,
}: Readonly<NoQuoteLockIconProps> = {}) {
  const t = useTranslations('HomePageV2.Prediction')
  const label = t('noPriceAvailableNow')

  const glyph = (
    <LockIcon
      width={size}
      height={size}
      color="var(--content-tertiary)"
      role="img"
      aria-label={label}
    />
  )

  // Render at the document root (`withinPortal`) instead of inline: sibling
  // price cells each create their own stacking context (`data-contract-btn` →
  // z-index:2), so an inline tooltip on the left cell is painted *under* the
  // right cell's border regardless of its own z-index. Portaling lifts it out
  // of every cell's context so it always sits on top.
  const tooltipProps = {
    label,
    events: { hover: true, focus: true, touch: true },
    withinPortal: true,
  }

  if (fillParent) {
    // Make the whole host cell the hover target (OG-9099 feedback): a
    // transparent `inset: 0` overlay is the tooltip's target, sizing against
    // the nearest positioned ancestor (the host cell). Locked cells are
    // non-interactive, so the overlay swallowing pointer events is harmless.
    return (
      <>
        {glyph}
        <Tooltip {...tooltipProps}>
          <Box
            aria-hidden
            data-testid="no-quote-lock-hover-target"
            style={{ position: 'absolute', inset: 0 }}
          />
        </Tooltip>
      </>
    )
  }

  return <Tooltip {...tooltipProps}>{glyph}</Tooltip>
}
